import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import type { PageViewport } from 'pdfjs-dist';

export interface ViewportReadyEvent {
  viewport: PageViewport;
  baseWidth: number;
  baseHeight: number;
}

@Component({
  selector: 'app-pdf-canvas',
  templateUrl: './pdf-canvas.component.html',
  styleUrls: ['./pdf-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PdfCanvasComponent implements OnChanges {
  @ViewChild('pdfCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;
  @Input() page = 1;
  @Input() scale = 1.5;
  @Input() rotation = 0;

  @Output() viewportReady = new EventEmitter<ViewportReadyEvent>();

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ((changes['pdfDoc'] || changes['page'] || changes['scale'] || changes['rotation']) && this.pdfDoc) {
      await this.render();
    }
  }

  private async render(): Promise<void> {
    if (!this.pdfDoc || !this.canvasRef?.nativeElement) return;

    const pdfPage = await this.pdfDoc.getPage(this.page);

    // Base (unscaled, unrotated) dimensions — the fixed reference frame for normalization.
    const baseViewport = pdfPage.getViewport({ scale: 1, rotation: 0 });
    const viewport = pdfPage.getViewport({ scale: this.scale, rotation: this.rotation });

    const canvas = this.canvasRef.nativeElement;
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const ctx = canvas.getContext('2d')!;
    await pdfPage.render({ canvasContext: ctx, viewport, canvas }).promise;

    this.viewportReady.emit({
      viewport,
      baseWidth: baseViewport.width,
      baseHeight: baseViewport.height,
    });
  }
}
